import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuFileSpreadsheet, LuLoaderCircle, LuTriangleAlert } from 'react-icons/lu';
import useBudgetText from '../budgetText';
import { FileDropZone, FileRow } from './formBits';

const NOTE = 'tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-px-3.5 tw-py-3 tw-text-sm tw-leading-relaxed';

/**
 * Body of "Excel'den Gelir / Gider İçe Aktar": drop zone, the chosen file,
 * parse errors, the rows to import (first 50) with their total, skipped
 * duplicates and the progress. The dialog keeps all the state and logic.
 */
const ImportPanel = ({
  kind, file, rows, parseError, isDragging, isChecking, isImporting, progress, duplicateInfo, totalAmount,
  inputRef, onDragOver, onDragLeave, onDrop, onInputChange, onRemoveFile,
}) => {
  const { label, formatTotal, formatDate, locale } = useBudgetText();
  const income = kind === 'income';

  if (!file) {
    return (
      <FileDropZone
        title={label(income ? 'import.dropIncome' : 'import.dropExpense')}
        hint={label('import.formats')}
        icon={LuFileSpreadsheet}
        isDragging={isDragging}
        inputRef={inputRef}
        accept=".xlsx,.xls,.csv"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onInputChange={onInputChange}
      />
    );
  }

  return (
    <div className="tw-space-y-3">
      <FileRow
        name={file.name}
        size={file.size}
        onRemove={onRemoveFile}
        disabled={isImporting}
        accent="tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300"
      />

      {parseError && (
        <div role="alert" className={`${NOTE} tw-bg-rose-500/10 tw-text-rose-800 dark:tw-text-rose-200`}>
          <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-rose-500" />
          <span>{parseError}</span>
        </div>
      )}

      {!parseError && rows.length > 0 && (
        <>
          <div className={`${NOTE} tw-bg-emerald-500/10 tw-text-emerald-800 dark:tw-text-emerald-200`}>
            <LuCircleCheck aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-500" />
            <span>{label('import.ready', { count: rows.length, total: formatTotal(totalAmount) })}</span>
          </div>

          <div className="tw-max-h-[260px] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
            <table className="tw-w-full tw-border-collapse tw-text-[13px]">
              <thead className="tw-sticky tw-top-0 tw-bg-slate-50 dark:tw-bg-[#262b34]">
                <tr>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label(income ? 'import.customer' : 'import.sender')}</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('import.date')}</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('import.amount')}</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('import.currencyShort')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/10">
                    <td className="tw-max-w-[16rem] tw-truncate tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-slate-800 dark:tw-text-slate-100" title={r.BütçeKaynak}>{r.BütçeKaynak}</td>
                    <td className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{formatDate(r.BütçeTarihi)}</td>
                    <td className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">
                      {Number(r.BütçeMiktarı).toLocaleString(locale)}
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-slate-600 dark:tw-text-slate-300">{r.ParaBirimi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 50 && (
              <p className="tw-border-t tw-border-slate-200/70 tw-px-3 tw-py-2 tw-text-center tw-text-xs tw-text-slate-500 dark:tw-border-white/10 dark:tw-text-slate-400">
                {label('import.more', { count: rows.length - 50 })}
              </p>
            )}
          </div>
        </>
      )}

      {isChecking && (
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          {label('import.checking')}
        </p>
      )}

      {!parseError && !isChecking && (duplicateInfo.existing > 0 || duplicateInfo.inFile > 0) && (
        <div className={`${NOTE} tw-bg-amber-500/10 tw-text-amber-900 dark:tw-text-amber-100`}>
          <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-amber-500" />
          <span className="tw-space-x-1">
            {duplicateInfo.existing > 0 && <span>{label('import.dupExisting', { count: duplicateInfo.existing })}</span>}
            {duplicateInfo.inFile > 0 && <span>{label('import.dupInFile', { count: duplicateInfo.inFile })}</span>}
            {rows.length === 0 && <span>{label('import.nothingNew')}</span>}
          </span>
        </div>
      )}

      {isImporting && (
        <div className="tw-space-y-1.5">
          <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{label('import.progress', { done: progress.done, total: progress.total })}</p>
          <span className="tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
            <span
              className="tw-block tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-emerald-500 tw-transition-[width] tw-duration-300"
              style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
            />
          </span>
        </div>
      )}
    </div>
  );
};

ImportPanel.propTypes = {
  kind: PropTypes.oneOf(['income', 'expense']).isRequired,
  file: PropTypes.object,
  rows: PropTypes.array.isRequired,
  parseError: PropTypes.string,
  isDragging: PropTypes.bool,
  isChecking: PropTypes.bool,
  isImporting: PropTypes.bool,
  progress: PropTypes.shape({ done: PropTypes.number, total: PropTypes.number }).isRequired,
  duplicateInfo: PropTypes.shape({ existing: PropTypes.number, inFile: PropTypes.number }).isRequired,
  totalAmount: PropTypes.number.isRequired,
  inputRef: PropTypes.object.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
};

export default ImportPanel;
